import { nanoid } from '@reduxjs/toolkit';
import type { AxisSeriesSettings, SplitSort } from '@/features/chartsView';
import {
  ChartType,
  DEFAULT_LEFT_AXIS_SERIES,
  DEFAULT_RIGHT_AXIS_SERIES,
  applyAxisMetric,
  applyAxisSeriesPatch,
  applyDefaultExcludedSplitOptions,
  applyExcludedSplitOptionsToggle,
  applySplitDimension,
  applySplitSortPatch,
  cohortChartDataConfigValidator,
  getRequestedMetrics,
  healAxesAgainstMeta,
  resetSplitSortToDefault,
} from '@/features/chartsView';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale, Lifetime } from '@/shared/types';
import * as CohortChartAPI from '../api/cohortChartAPI';
import type { CohortChartConfig, CohortDataResponse, ChartMeta } from './types';

export interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CohortChartConfig;
  isConfigInitialized: boolean;
  splitSortBySession: Record<string, SplitSort>;
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  isConfigInitialized: false,
  splitSortBySession: {},
  chart: {
    id: '',
    type: ChartType.cohort,
    dateScale: DateScale.DAY,
    dateRange: { preset: DateRangePresets.LAST_90_DAYS },
    lifetime: Lifetime.YEAR,
    probabilisticAttribution: true,
    splitDimension: '',
    primaryMetric: '',
    secondaryMetric: '',
    excludedSplitOptions: [],
    syncedYAxes: false,
    highlightLineOnHover: true,
    filters: [],
    leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
    rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
  },
};

const buildVendorDefault = (
  meta: ChartMeta,
  lifetimeTwoYearsExtension: boolean,
): CohortChartConfig => ({
  id: nanoid(),
  type: ChartType.cohort,
  excludedSplitOptions: [],
  syncedYAxes: true,
  highlightLineOnHover: true,
  filters: [],
  dateScale: DateScale.DAY,
  dateRange: { preset: DateRangePresets.LAST_90_DAYS },
  lifetime: lifetimeTwoYearsExtension ? Lifetime.TWO_YEARS : Lifetime.YEAR,
  probabilisticAttribution: true,
  splitDimension: meta.defaultAxis.dimension,
  primaryMetric: meta.defaultAxis.primaryMetric,
  secondaryMetric: meta.defaultAxis.secondaryMetric,
  leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
  rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
});

export const cohortChartSlice = createAppSlice({
  name: 'cohortChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
          healAxesAgainstMeta(state);
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.cohortChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.dateScale = action.payload;
    }),
    updateDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.dateRange = action.payload;
      },
    ),
    updateLifetime: create.reducer<number>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.lifetime = action.payload;
    }),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      applySplitDimension(state, action.payload);
    }),
    updateSplitSort: create.reducer<Partial<SplitSort>>((state, action) => {
      applySplitSortPatch(state, action.payload);
    }),
    resetSplitSort: create.reducer((state) => {
      resetSplitSortToDefault(state);
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      applyAxisMetric(state, 'primary', action.payload);
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      applyAxisMetric(state, 'secondary', action.payload);
    }),
    updateAxisSeries: create.reducer<{
      axis: 'left' | 'right';
      patch: Partial<AxisSeriesSettings>;
    }>((state, action) => {
      applyAxisSeriesPatch(state, action.payload.axis, action.payload.patch);
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateHighlightLineOnHover: create.reducer<boolean>((state, action) => {
      state.chart.highlightLineOnHover = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CohortChartConfig>((state, action) => {
      const parsed = cohortChartDataConfigValidator.safeParse(action.payload);

      state.chart = parsed.success ? parsed.data : action.payload;
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    setupVendorDefault: create.reducer<{ lifetimeTwoYearsExtension: boolean }>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          return;
        }

        state.chart = buildVendorDefault(
          state.meta.data,
          action.payload.lifetimeTwoYearsExtension,
        );
        state.isConfigInitialized = true;
        healAxesAgainstMeta(state);
      },
    ),
    setupVendorDefaultWithOverride: create.reducer<{
      lifetimeTwoYearsExtension: boolean;
      override: Partial<CohortChartConfig>;
    }>((state, action) => {
      if (!isMetaFinished(state.meta)) {
        return;
      }

      state.chart = {
        ...buildVendorDefault(
          state.meta.data,
          action.payload.lifetimeTwoYearsExtension,
        ),
        ...action.payload.override,
      };
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        applyDefaultExcludedSplitOptions(state, action.payload);
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      applyExcludedSplitOptionsToggle(
        state,
        action.payload.options,
        action.payload.checked,
      );
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
  },
});

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (params: CohortChartConfig): AppThunk<Promise<CohortDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    const { meta } = state.cohortChart;

    if (!isMetaFinished(meta)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { dimensionsFilters, metricsFilters } = prepareFilters(
      params.filters,
      meta.data.metrics,
      meta.data.dimensions,
    );

    return CohortChartAPI.getCohortData({
      split: params.splitDimension,
      metrics: getRequestedMetrics(params),
      dateScale: params.dateScale,
      dateRange: prepareDateRangeValueObject(params.dateRange),
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const {
  initMeta,
  updateDateScale,
  updateDateRange,
  updateLifetime,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updateSplitSort,
  resetSplitSort,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateAxisSeries,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateHighlightLineOnHover,
  updateFilters,
  updateChart,
  setupVendorDefault,
  setupVendorDefaultWithOverride,
  toggleExcludedSplitOptions,
  setupDefaultExcludedSplitOptions,
} = cohortChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = cohortChartSlice.selectors;
